import {
	Grid,
	Typography,
	Radio,
	RadioGroup,
	FormControlLabel
} from '@mui/material';
import { ItemsAutocomplete } from '@components/Forms';
import React, { useMemo } from 'react';
import { IProject } from 'types/users';
import { IConsoleAuth } from 'types/login';
import { AuthContext } from 'hooks/useAuth';
import { IProjectData, ProjectContext } from 'routes/ProjectProvider';
import { isEmpty } from 'lodash';

interface IPermittedProjectsProps {
	formik: any;
	enabled: boolean;
}

const PermittedProjects: React.FC<IPermittedProjectsProps> = ({
	formik,
	enabled
}) => {
	const { consoleUser } = React.useContext<IConsoleAuth>(AuthContext);
	const { currentProject } = React.useContext<IProjectData>(ProjectContext);
	const currentCompanyId = useMemo(
		() =>
			consoleUser?.projects?.find((p) => p.id === currentProject)?.companyId ||
			'',
		[consoleUser?.projects, currentProject]
	);

	const projects = useMemo(
		() =>
			consoleUser?.projects?.filter((p) => p.companyId === currentCompanyId),
		[consoleUser.projects, currentCompanyId]
	);
	const projectsMap = useMemo(() => {
		const res: Record<string, string> = {};
		projects?.forEach((p) => {
			if (p.companyId === currentCompanyId) {
				res[p.id] = p.name;
			}
		});
		return res;
	}, [projects, currentCompanyId]);

	const allProjects =
		formik.values.allProjects === undefined
			? isEmpty(formik.values.permittedProjects)
			: formik.values.allProjects;

	return (
		<Grid item xs={12} data-testid="access-key-project-mapping">
			<Typography pl={1} py={1}>
				{intl.tr('dialog.permitted.project.header')}
			</Typography>
			<RadioGroup name="permitted-projects-radio-group" value={allProjects}>
				<FormControlLabel
					value="true"
					disabled={!enabled}
					control={<Radio />}
					checked={allProjects}
					onChange={() => {
						formik.setFieldValue('allProjects', true);
					}}
					label={intl.tr('dialog.permitted.project.all')}
				/>
				<FormControlLabel
					value="false"
					disabled={!enabled}
					checked={!allProjects}
					control={<Radio />}
					onChange={() => {
						formik.setFieldValue('allProjects', false);
					}}
					label={intl.tr('dialog.permitted.project.selected')}
				/>
				<Grid container p={3} pl={4}>
					<ItemsAutocomplete
						id="permitted-projects-dropdown"
						disabled={allProjects || !enabled}
						multiple
						values={(formik.values.permittedProjects || []).map(
							(p: string) => ({ name: projectsMap[p], id: p })
						)}
						data={(projects || ([] as IProject[])).map((p) => ({
							name: p.name,
							id: p.id
						}))}
						textLabel={intl.tr('dialog.permitted.project.placeholder')}
						onChange={async (v1) => {
							formik.setFieldValue(
								'permittedProjects',
								v1.map((v2) => v2.id)
							);
						}}
					/>
				</Grid>
			</RadioGroup>
		</Grid>
	);
};

export type { IPermittedProjectsProps };
export default PermittedProjects;
